import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useOtpSendMutation, useOtpVerificationMutation } from "../../Redux/Api/Signup";
import { LoginResponse, SignupWait } from "../../Redux/reducer/LoginReducer";
import { consumeNextAfterLogin } from "../../utils/nextAfterLogin";
import { requestFCMToken } from "../../utils/firebaseUtils";
import { useSaveFcmTokenMutation } from "../../Redux/Api/Notifications";
import useCiT from "../../common/ci/i18n";
import { AuthHeading, AuthShell } from "./AuthShell";
import { OtpBoxes } from "./OtpBoxes";

const maskPhone = (phone: string) =>
  phone && phone.length >= 10 ? `${phone.slice(0, 3)}xxxxx${phone.slice(-2)}` : phone;

/** Signup ke baad phone verify — 6 ank ka OTP. */
const OTPForm: React.FC = () => {
  const t = useCiT();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const state = (location?.state || {}) as {
    phoneNumber?: string;
    name?: string;
    password?: string;
  };
  const { phoneNumber, name, password } = state;

  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(0);

  const [mutation, { data, isLoading, isSuccess, isError, error }] =
    useOtpVerificationMutation();
  const [
    otpSendMutation,
    {
      data: otpSendData,
      isError: otpSendError,
      isSuccess: otpSendSuccess,
      isLoading: otpSendLoading,
      error: otpError,
    },
  ] = useOtpSendMutation();
  const [saveFcmToken] = useSaveFcmTokenMutation();

  // seedha URL khol liya (state ke bina) to wapas signup par
  useEffect(() => {
    if (!phoneNumber) navigate("/signup", { replace: true });
  }, [phoneNumber, navigate]);

  // Notification permission verify hone ke baad — pehle nahi (popup se bachne ke liye).
  const registerFcm = async (userId?: string) => {
    if (!userId) return;
    try {
      const token: any | null = await requestFCMToken();
      if (token) saveFcmToken({ userId, token });
    } catch (e) {
      console.error("Error fetching FCM Token:", e);
    }
  };

  // ---- resend timer (60 s) ----
  useEffect(() => {
    const start = Number(localStorage.getItem("otpTimerStart") || 0);
    const remaining = start ? 60 - Math.floor((Date.now() - start) / 1000) : 0;
    setTimer(remaining > 0 ? remaining : 0);
  }, []);

  useEffect(() => {
    if (timer <= 0) return;
    const id = setTimeout(() => setTimer((p) => p - 1), 1000);
    return () => clearTimeout(id);
  }, [timer]);

  const handleResendOTP = () => {
    if (timer === 0 && !otpSendLoading) otpSendMutation({ phoneNumber, name, password });
  };

  useEffect(() => {
    if (otpSendSuccess) {
      toast.success(otpSendData?.message || t("OTP dobara bhej diya"));
      localStorage.setItem("otpTimerStart", Date.now().toString());
      setTimer(60);
      setOtp("");
    } else if (otpSendError) {
      toast.error(otpError?.data?.message || t("Kuch galat ho gaya"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpSendError, otpSendSuccess]);

  // ---- verify ----
  useEffect(() => {
    if (isSuccess && data?.token) {
      localStorage.setItem("jwt", data.token);
      dispatch(SignupWait(true));
      dispatch(LoginResponse(data.contractorData));
      toast.success(data?.message || t("Number verify ho gaya"));
      registerFcm(data?.contractorData?._id);
      navigate(consumeNextAfterLogin());
    } else if (isError) {
      toast.error(error?.data?.message || t("OTP galat hai — dobara try karein"));
      setOtp("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError, data, dispatch, navigate]);

  const submit = (code?: string) => {
    const value = code || otp;
    if (value.length !== 6) {
      toast.error(t("6 ank ka OTP daalein"));
      return;
    }
    mutation({ phoneNumber, otp: value });
  };

  if (!phoneNumber) return null;

  return (
    <AuthShell standalone>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        noValidate
      >
        <AuthHeading
          title={t("OTP daalein")}
          sub={
            <>
              {t("6 ank ka code bheja hai")}{" "}
              <span className="ci-num font-semibold text-ci-ink">
                +91 {maskPhone(String(phoneNumber))}
              </span>{" "}
              {t("par.")}{" "}
              <button
                type="button"
                onClick={() => navigate("/signup")}
                className="font-semibold text-ci-navy underline underline-offset-2"
              >
                {t("Number badlein")}
              </button>
            </>
          }
        />

        <div className="my-6">
          <OtpBoxes
            value={otp}
            onChange={setOtp}
            onComplete={(full) => submit(full)}
            disabled={isLoading}
          />
        </div>

        <button
          type="submit"
          disabled={isLoading || otp.length !== 6}
          className="inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-md border border-ci-orange bg-ci-orange text-[17px] font-bold uppercase tracking-wide text-white transition hover:border-ci-orange2 hover:bg-ci-orange2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isLoading ? (
            <>
              <FaSpinner className="animate-spin" />
              {t("Check kar rahe hain…")}
            </>
          ) : (
            t("Verify karein")
          )}
        </button>

        <p className="mt-4 text-center text-[14px] text-ci-muted">
          {t("OTP nahi aaya?")}{" "}
          <button
            type="button"
            onClick={handleResendOTP}
            disabled={timer > 0 || otpSendLoading}
            className={`font-semibold underline-offset-2 ${
              timer > 0 || otpSendLoading
                ? "cursor-not-allowed text-[#9aa0a6]"
                : "text-ci-navy hover:underline"
            }`}
          >
            {timer > 0 ? `${t("Dobara bhejein")} (${timer}s)` : t("Dobara bhejein")}
          </button>
        </p>
      </form>
    </AuthShell>
  );
};

export default OTPForm;
